<template>
  <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
    <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-1">Custom Domain</h2>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
      Serve your trust center on your own hostname, such as
      <code>trust.yourcompany.com</code>. You point a DNS record at us, we issue
      and renew the TLS certificate.
    </p>

    <div v-if="loading" class="text-sm text-gray-500 dark:text-gray-400">Loading…</div>

    <!-- No domain configured yet -->
    <div v-else-if="!domain">
      <div class="flex flex-col sm:flex-row gap-2">
        <input
          v-model="input"
          type="text"
          class="input flex-1"
          placeholder="trust.yourcompany.com"
          :disabled="saving"
          @keyup.enter="save"
        />
        <button
          type="button"
          class="btn-primary whitespace-nowrap"
          :disabled="saving || !input"
          @click="save"
        >{{ saving ? 'Saving…' : 'Add domain' }}</button>
      </div>
      <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
        Use a subdomain you control. A bare domain like <code>yourcompany.com</code>
        works only if your DNS provider supports ALIAS or CNAME flattening at the root.
      </p>
    </div>

    <!-- Domain configured -->
    <div v-else class="space-y-4">
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div class="flex items-center gap-2">
          <a
            v-if="domain.status === 'active'"
            :href="`https://${domain.domain}`"
            target="_blank"
            rel="noopener"
            class="font-mono text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >{{ domain.domain }}</a>
          <span v-else class="font-mono text-sm text-gray-900 dark:text-white">{{ domain.domain }}</span>
          <span :class="badgeClass">{{ statusLabel }}</span>
        </div>
        <button
          type="button"
          class="btn-secondary btn-sm text-red-600 dark:text-red-400"
          :disabled="saving"
          @click="remove"
        >Remove</button>
      </div>

      <!-- Waiting for the customer's DNS -->
      <div
        v-if="domain.status === 'pending_dns' || domain.status === 'failed'"
        class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md"
      >
        <p class="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
          Add this record at your DNS provider
        </p>
        <dl class="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-xs">
          <dt class="text-blue-700 dark:text-blue-300">Type</dt>
          <dd class="font-mono text-blue-900 dark:text-blue-100">CNAME</dd>
          <dt class="text-blue-700 dark:text-blue-300">Name</dt>
          <dd class="font-mono text-blue-900 dark:text-blue-100 break-all">{{ recordName }}</dd>
          <dt class="text-blue-700 dark:text-blue-300">Value</dt>
          <dd class="font-mono text-blue-900 dark:text-blue-100 break-all">{{ domain.cname_target || cnameTarget }}</dd>
        </dl>
        <p class="mt-3 text-xs text-blue-700 dark:text-blue-300">
          Some providers want just <code>{{ recordName }}</code> and others the full
          hostname; both are the same record. Once it is live, check below — the
          certificate can only be issued after the hostname resolves to us.
        </p>
        <button
          type="button"
          class="btn-primary btn-sm mt-3"
          :disabled="verifying"
          @click="verify"
        >{{ verifying ? 'Checking…' : 'Check DNS and issue certificate' }}</button>
      </div>

      <!-- Certificate being issued -->
      <div
        v-else-if="domain.status === 'provisioning'"
        class="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md"
      >
        <p class="text-sm font-medium text-yellow-900 dark:text-yellow-100">
          Issuing the TLS certificate
        </p>
        <p class="mt-1 text-xs text-yellow-800 dark:text-yellow-200">
          This usually takes a few minutes and can take up to an hour. You can
          leave this page; the domain goes live on its own. Keep the DNS record
          in place — removing it will fail the certificate.
        </p>
      </div>

      <!-- Live -->
      <div
        v-else-if="domain.status === 'active'"
        class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md"
      >
        <p class="text-sm text-green-900 dark:text-green-100">
          Live over HTTPS. Leave the DNS record in place so the certificate can renew.
        </p>
      </div>

      <p v-if="domain.failure_reason" class="text-xs text-red-600 dark:text-red-400 break-words">
        {{ domain.failure_reason }}
      </p>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import { getApiBase, authHeaders } from '@/utils/api'

export default {
  name: 'CustomDomainSettings',
  setup() {
    const store = useStore()
    const loading = ref(true)
    const saving = ref(false)
    const verifying = ref(false)
    const domain = ref(null)
    const cnameTarget = ref('')
    const input = ref('')
    let poller = null

    const tenantId = () => store.state.currentTenantId
    const endpoint = () => `${getApiBase()}/admin/tenants/${tenantId()}/custom-domain`

    const notify = (type, message) =>
      store.dispatch('notification/showNotification', { type, message, duration: 5000 })

    const statusLabel = computed(() => ({
      pending_dns: 'DNS needed',
      provisioning: 'Issuing certificate',
      active: 'Live',
      failed: 'Failed'
    }[domain.value?.status] || domain.value?.status))

    const badgeClass = computed(() => {
      const base = 'px-2 py-0.5 rounded-full text-xs font-medium'
      return {
        active: `${base} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300`,
        provisioning: `${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300`,
        failed: `${base} bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300`
      }[domain.value?.status] ||
        `${base} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`
    })

    // The label the tenant types into their DNS provider, i.e. the hostname
    // minus its registrable domain. Naive on multi-part suffixes like co.uk,
    // which is why the UI also shows the full hostname as an alternative.
    const recordName = computed(() => {
      const parts = (domain.value?.domain || '').split('.')
      return parts.length > 2 ? parts.slice(0, parts.length - 2).join('.') : domain.value?.domain
    })

    const stopPolling = () => {
      if (poller) {
        clearInterval(poller)
        poller = null
      }
    }

    // Only poll while a certificate is actually being issued: the GET refreshes
    // state from GCP, so polling any other status is a pointless API call.
    const startPolling = () => {
      if (poller || domain.value?.status !== 'provisioning') return
      poller = setInterval(async () => {
        await load(true)
        if (domain.value?.status !== 'provisioning') stopPolling()
      }, 20000)
    }

    const load = async (quiet = false) => {
      if (!tenantId()) return
      try {
        if (!quiet) loading.value = true
        const resp = await axios.get(endpoint(), authHeaders(store))
        domain.value = resp.data?.domain === null ? null : resp.data
        cnameTarget.value = resp.data?.cname_target || cnameTarget.value
        startPolling()
      } catch (err) {
        if (!quiet) notify('error', err.response?.data?.error || 'Failed to load custom domain')
      } finally {
        loading.value = false
      }
    }

    const save = async () => {
      try {
        saving.value = true
        const resp = await axios.post(endpoint(), { domain: input.value.trim() }, authHeaders(store))
        domain.value = resp.data
        input.value = ''
        notify('success', 'Domain saved. Add the DNS record shown, then check it.')
      } catch (err) {
        notify('error', err.response?.data?.error || 'Failed to save domain')
      } finally {
        saving.value = false
      }
    }

    const verify = async () => {
      try {
        verifying.value = true
        const resp = await axios.post(`${endpoint()}/verify`, {}, authHeaders(store))
        domain.value = resp.data
        notify('success', 'DNS looks right. The certificate is being issued.')
        startPolling()
      } catch (err) {
        // A 409 is the expected answer while DNS has not propagated, and it
        // carries the refreshed record so the reason can be shown in place.
        if (err.response?.status === 409 && err.response.data?.domain) {
          domain.value = err.response.data.domain
        }
        notify('error', err.response?.data?.error || 'Could not verify the domain')
      } finally {
        verifying.value = false
      }
    }

    const remove = async () => {
      if (!window.confirm(`Stop serving ${domain.value.domain}? Its certificate will be deleted.`)) return
      try {
        saving.value = true
        await axios.delete(endpoint(), authHeaders(store))
        domain.value = null
        stopPolling()
        notify('success', 'Custom domain removed')
      } catch (err) {
        notify('error', err.response?.data?.error || 'Failed to remove domain')
      } finally {
        saving.value = false
      }
    }

    onMounted(load)
    onUnmounted(stopPolling)

    return {
      loading, saving, verifying, domain, cnameTarget, input,
      statusLabel, badgeClass, recordName, save, verify, remove
    }
  }
}
</script>
